/**
 * `/casos/:id/conferencia-final` (E169.F1, GUS-113 e GUS-215, ADR-0117): "Conferir e assinar". A
 * versão da análise, as saídas desta versão com a marca de revisão do perito, as revisões do perito
 * em vigor e as que faltam, e a confirmação que leva o caso à assinatura. É o destino do passo
 * "Assinar o laudo" do Próximo passo, no lugar do link direto para a tela do certificador.
 * E207.F5.T02 (owner, 01/11): a faixa "Próximo passo" saiu daqui (a aba Pendências da mesa já a
 * mostra) e "Voltar ao caso" leva à mesa.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  ConfirmarConferencia,
  RevisoesDoPerito,
  SaidaDaVersao,
} from '@/components/ConferenciaFinal';
import { ConferenciaFinalSkeleton } from '@/components/skeletons';
import { FalhaComSaida } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  ConferenciaFinalError,
  lerConferenciaFinal,
  type ConferenciaFinalData,
} from '@/lib/conferencia_final';

const t = ptBR.conferenciaFinal;

type Falha = 'sem_permissao' | 'nao_encontrado' | 'indisponivel';

function falhaDe(err: unknown): Falha {
  if (err instanceof ConferenciaFinalError) {
    if (err.motivo === 'sem_permissao' || err.motivo === 'nao_encontrado') return err.motivo;
  }
  return 'indisponivel';
}

const TEXTO_DA_FALHA: Record<Falha, string> = {
  sem_permissao: t.semPermissao,
  nao_encontrado: t.naoEncontrado,
  indisponivel: t.erro,
};

function Cabecalho({ conferencia }: { conferencia: ConferenciaFinalData }) {
  return (
    <p className="text-body-sm text-fg-muted" data-testid="versao-conferida">
      <span className="font-medium text-fg">{conferencia.display_code}</span>
      {' · '}
      {conferencia.versao != null
        ? t.versao.replace('{versao}', String(conferencia.versao))
        : t.semVersao}
      {' · '}
      {t.modo[conferencia.modo]}
    </p>
  );
}

function Conteudo({
  caseId,
  conferencia,
  reler,
}: {
  caseId: string;
  conferencia: ConferenciaFinalData;
  reler: () => void;
}) {
  const client = useApiClient();
  return (
    <>
      <Cabecalho conferencia={conferencia} />
      <section
        className="border border-border bg-surface p-sc-4"
        aria-labelledby="saidas-desta-versao"
      >
        <h2 id="saidas-desta-versao" className="text-body-lg font-semibold text-fg">
          {t.saidas.titulo}
        </h2>
        <ul className="mt-sc-2">
          {conferencia.saidas.map((s) => (
            <SaidaDaVersao
              key={`${s.saida}-${s.artefato_id ?? 'sem'}-${s.situacao}`}
              client={client}
              caseId={caseId}
              saida={s}
              onMarcada={reler}
            />
          ))}
        </ul>
      </section>
      <RevisoesDoPerito conferencia={conferencia} />
      <ConfirmarConferencia
        client={client}
        caseId={caseId}
        conferencia={conferencia}
        onConfirmada={reler}
      />
    </>
  );
}

export function ConferenciaFinalPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const [conferencia, setConferencia] = useState<ConferenciaFinalData | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setConferencia(await lerConferenciaFinal(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(falhaDe(err));
    }
  }, [client, caseId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <section
      className="mx-auto flex max-w-4xl flex-col gap-sc-6 p-sc-6"
      data-testid="conferencia-final-page"
    >
      <header>
        <p className="text-caption uppercase tracking-[0.1em] text-fg-muted">{t.eyebrow}</p>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.subtitulo}</p>
        <nav className="mt-sc-2 text-body-sm">
          <Link
            className="text-accent-700"
            to={`/casos/${caseId}/mesa`}
            data-testid="conferencia-final-voltar"
          >
            {t.voltarAoCaso}
          </Link>
        </nav>
      </header>
      {falha !== null ? (
        // E200.F3 (GUS-220, ADR-0143 §4): a falha com saída — tentar de novo ou voltar à mesa.
        <FalhaComSaida
          mensagem={TEXTO_DA_FALHA[falha]}
          onTentarDeNovo={() => void carregar()}
          voltar={{ para: `/casos/${caseId}/mesa`, rotulo: t.voltarAoCaso }}
          data-testid="conferencia-final-erro"
        />
      ) : null}
      {conferencia === null && falha === null ? (
        <ConferenciaFinalSkeleton label={t.carregando} />
      ) : null}
      {conferencia !== null ? (
        <Conteudo caseId={caseId} conferencia={conferencia} reler={() => void carregar()} />
      ) : null}
    </section>
  );
}
